import { useEffect, useRef, useState } from "react";
import type { Rec } from "@shared/types.js";
import { computeStats } from "@shared/stats.js";
import { fnum } from "../lib/format.js";
import { COMP_ORDER, COMP_LABEL, CATS } from "../lib/comps.js";
import {
  type FilterState, STATUS_OPTIONS, isDefault, uniqValues, emptyFilters,
} from "../lib/filters.js";

export function FilterBar({
  all, rows, filters, onChange,
}: {
  all: Rec[];
  rows: Rec[];
  filters: FilterState;
  onChange: (f: FilterState) => void;
}) {
  const s = computeStats(rows);
  const set = (patch: Partial<FilterState>) => onChange({ ...filters, ...patch });

  /* The bar stays docked under the navbar, so the filters and the counts they
     shape are readable from anywhere on the page. A sentinel just above it says
     when the bar has left its resting place, which is when it earns a shadow to
     lift it off the content scrolling underneath. */
  const sentinel = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const navH = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue("--nav-h"),
    ) || 72;
    const io = new IntersectionObserver(
      ([e]) => setStuck(!(e?.isIntersecting ?? true)),
      { rootMargin: `-${navH}px 0px 0px 0px`, threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const toggleIn = (setRef: Set<string>, v: string): Set<string> => {
    const next = new Set(setRef);
    if (next.has(v)) next.delete(v); else next.add(v);
    return next;
  };

  /* Each active filter as a chip that can be cleared on its own. */
  const chips: { key: string; label: string; clear: () => void }[] = [];
  if (filters.comps.size) {
    chips.push({
      key: "comp",
      label: [...filters.comps].map((c) => COMP_LABEL[c as keyof typeof COMP_LABEL]).join(", "),
      clear: () => set({ comps: new Set() }),
    });
  }
  if (filters.cats.size) {
    chips.push({
      key: "cat",
      label: `Cat ${[...filters.cats].sort().join("/")}`,
      clear: () => set({ cats: new Set() }),
    });
  }
  if (filters.region) {
    chips.push({ key: "region", label: filters.region, clear: () => set({ region: "" }) });
  }
  if (filters.status) {
    chips.push({
      key: "status",
      label: STATUS_OPTIONS.find(([v]) => v === filters.status)?.[1] ?? filters.status,
      clear: () => set({ status: "" }),
    });
  }
  if (filters.rateType) {
    chips.push({ key: "rate", label: filters.rateType, clear: () => set({ rateType: "" }) });
  }
  if (filters.pic) {
    chips.push({ key: "pic", label: `PIC ${filters.pic}`, clear: () => set({ pic: "" }) });
  }
  if (filters.search) {
    chips.push({
      key: "q",
      label: `“${filters.search}”`,
      clear: () => set({ search: "" }),
    });
  }
  if (filters.includeCatE) {
    chips.push({
      key: "cate",
      label: "incl. Cat. E sheet",
      clear: () => set({ includeCatE: false }),
    });
  }

  const summary = (
    <>
      <b>{fnum(rows.length)}</b> records &middot;{" "}
      <b>{fnum(s.priced)}</b> priced &middot;{" "}
      <b>{fnum(s.estimated)}</b> estimated &middot;{" "}
      <b>{fnum(s.awaiting + s.estOutstanding)}</b> outstanding
    </>
  );

  return (
    <>
    <div ref={sentinel} className="filters-sentinel" aria-hidden="true" />
    <section className={`filters${stuck ? " stuck" : ""}`}>
      <div className="wrap">
        <div className="frow">
          <div className="fgroup">
            <label id="lab-type">Type</label>
            <div className="seg" role="group" aria-labelledby="lab-type">
              <button
                type="button" aria-pressed={filters.comps.size === 0}
                onClick={() => set({ comps: new Set() })}
              >
                All
              </button>
              {COMP_ORDER.map((c) => (
                <button
                  key={c} type="button" aria-pressed={filters.comps.has(c)}
                  onClick={() => set({ comps: toggleIn(filters.comps, c) })}
                >
                  {COMP_LABEL[c]}
                </button>
              ))}
              <button
                type="button" aria-pressed={filters.includeCatE}
                title="The Cat. E sheet holds 217 extra accommodation rows that the workbook's Overview tab does not count"
                onClick={() => set({ includeCatE: !filters.includeCatE })}
              >
                + Cat. E sheet
              </button>
            </div>
          </div>

          <div className="fgroup">
            <label id="lab-cat">Category</label>
            <div className="seg" role="group" aria-labelledby="lab-cat">
              <button
                type="button" aria-pressed={filters.cats.size === 0}
                onClick={() => set({ cats: new Set() })}
              >
                All
              </button>
              {CATS.map((c) => (
                <button
                  key={c} type="button" aria-pressed={filters.cats.has(c)}
                  onClick={() => set({ cats: toggleIn(filters.cats, c) })}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="fgroup">
            <label htmlFor="f-region">Region</label>
            <select
              id="f-region" className={filters.region ? "on" : undefined} value={filters.region}
              onChange={(e) => set({ region: e.target.value })}
            >
              <option value="">All regions</option>
              {uniqValues(all, ["region"]).map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>

          <div className="fgroup">
            <label htmlFor="f-status">Status</label>
            <select
              id="f-status" className={filters.status ? "on" : undefined} value={filters.status}
              onChange={(e) => set({ status: e.target.value })}
            >
              {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          <div className="fgroup">
            <label htmlFor="f-ratetype">Rate type</label>
            <select
              id="f-ratetype" className={filters.rateType ? "on" : undefined} value={filters.rateType}
              onChange={(e) => set({ rateType: e.target.value })}
            >
              <option value="">All rate types</option>
              {uniqValues(all, ["rateType"]).map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>

          <div className="fgroup">
            <label htmlFor="f-pic">PIC</label>
            <select
              id="f-pic" className={filters.pic ? "on" : undefined} value={filters.pic}
              onChange={(e) => set({ pic: e.target.value })}
            >
              <option value="">All PICs</option>
              {uniqValues(all, ["nrPic", "estPic", "ccBy"]).map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>

          <div className="fspacer">
            {!isDefault(filters) && (
              <button className="chip-clear" type="button" onClick={() => onChange(emptyFilters())}>
                Reset filters
              </button>
            )}
          </div>
        </div>

        <div className="matchline">
          <span className="matchline-sum">{summary} in scope</span>
          {chips.map((c) => (
            <button
              key={c.key} className="fchip" type="button" onClick={c.clear}
              title={`Clear ${c.label}`}
            >
              {c.label}<span className="x">&times;</span>
            </button>
          ))}
        </div>
      </div>
    </section>
    </>
  );
}
